import { ChangeEvent, useState } from 'react'
import { useController } from 'react-hook-form'
import { FormControl, FormGroup, FormHelperText, FormLabel } from '@mui/material'
import { clone } from 'lodash'

import { DefaultElementProps } from 'common/interfaces'
import {
  CheckboxGroup as CheckboxGroupProps,
  CheckboxGroupOption,
} from 'elements/CheckboxGroup/interface'
import { CheckboxImageOption } from 'elements/CheckboxGroupImage/interface'
import { validationToCheckboxRules } from 'utils/validationsToRules'

import { ImageLogicGroupProps } from '../RadioGroupWrapper/RadioGroupWrapper'
import { SizingContainer } from '../SizingContainer'

type CheckboxGroupWrapperProps = Omit<CheckboxGroupProps, 'options'> &
  DefaultElementProps &
  ImageLogicGroupProps & {
    renderOption: (
      option: CheckboxGroupOption | CheckboxImageOption,
      valueState: Record<string, boolean>,
      handleChange: (id: string, event: ChangeEvent<HTMLInputElement>) => void
    ) => JSX.Element
    options: CheckboxGroupOption[] | CheckboxImageOption[]
    defaultValue: Record<any, any>
  }

export default function CheckboxGroupWrapper({
  name,
  size,
  label,
  options,
  direction,
  validations,
  control,
  renderOption,
  defaultValue,
  onChange,
  onFocus,
  gapSize,
  textAlign = 'left',
  classNames,
}: CheckboxGroupWrapperProps): JSX.Element {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue,
    rules: validationToCheckboxRules(validations),
  })

  const [value, setValue] = useState<Record<string, boolean>>(field.value)

  function handleChange(id: string, event: ChangeEvent<HTMLInputElement>): void {
    const newValue = clone(value)
    newValue[id] = event.target.checked

    field.onChange(newValue)
    onChange?.({ value: newValue })
    setValue(newValue)
  }

  const errorMessageId = `${name}-helper-text`

  return (
    <SizingContainer className={classNames} size={size}>
      <FormControl error={error !== undefined} sx={{ width: 1 }} onFocus={onFocus}>
        {label !== undefined && (
          <FormLabel id={`${name}-label`} required={validations?.required?.value}>
            {label}
          </FormLabel>
        )}
        <FormGroup
          role="group"
          aria-labelledby={label !== undefined ? `${name}-label` : undefined}
          aria-required={validations?.required?.value}
          aria-invalid={error !== undefined}
          aria-describedby={error !== undefined ? errorMessageId : undefined}
          row={direction === 'row'}
          sx={{
            gap: gapSize,
            '& .image-card-txt-wrap': {
              textAlign,
            },
          }}
        >
          {options.map((option) => renderOption(option, value, handleChange))}
        </FormGroup>
        {error !== undefined && (
          <FormHelperText id={errorMessageId} role="alert">
            {error.message}
          </FormHelperText>
        )}
      </FormControl>
    </SizingContainer>
  )
}
